// Nouns are CC0 pixel-art NFT characters from nouns.wtf. Each preset pairs a
// Noun id with a Tailwind background color so the rendered avatar pops at
// small sizes.

export type AvatarPreset = {
  nounId: number;
  bgClass: string;
};

const NOUN_RANGE = 1800;

const BG_PALETTE = [
  "bg-red-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-yellow-500",
  "bg-lime-500",
  "bg-green-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-cyan-500",
  "bg-sky-500",
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-purple-500",
  "bg-fuchsia-500",
  "bg-pink-500",
  "bg-rose-500",
  "bg-red-600",
  "bg-orange-600",
  "bg-emerald-600",
  "bg-cyan-600",
  "bg-blue-600",
  "bg-violet-600",
  "bg-pink-600",
  "bg-rose-600",
  "bg-amber-400",
  "bg-lime-400",
  "bg-sky-400",
  "bg-purple-400",
  "bg-fuchsia-400",
];

export const nounImageUrl = (nounId: number): string => `/api/avatars/noun/${nounId}.svg`;

export const presetId = (preset: AvatarPreset): string => `noun-${preset.nounId}`;
export const presetLabel = (preset: AvatarPreset): string => `Noun #${preset.nounId}`;

const NOUN_ID_RE = /^noun-(\d+)$/;
export const decodePresetId = (id: string): number | null => {
  const match = NOUN_ID_RE.exec(id);
  if (!match) return null;
  const nounId = Number(match[1]);
  return Number.isInteger(nounId) && nounId >= 1 && nounId <= NOUN_RANGE ? nounId : null;
};

const PRESET_NOUN_IDS = [
  7, 42, 88, 137, 195, 256, 314, 372, 420, 486, 555, 620, 688, 741, 808, 872, 933, 1001, 1066, 1129,
  1184, 1247, 1308, 1372, 1435, 1498, 1561, 1620, 1700, 1777,
];

export const AVATAR_PRESETS: AvatarPreset[] = PRESET_NOUN_IDS.map((nounId, index) => ({
  nounId,
  bgClass: BG_PALETTE[index % BG_PALETTE.length] as string,
}));

const PRESETS_BY_NOUN_ID = new Map(AVATAR_PRESETS.map((p) => [p.nounId, p]));

const hashString = (input: string): number => {
  let hash = 0;
  for (let index = 0; index < input.length; index++) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

// Distinct hash for the color so it doesn't correlate with the noun-id pick.
const bgForSeed = (seed: string): string =>
  BG_PALETTE[hashString(`${seed}:bg`) % BG_PALETTE.length] as string;

export const findPreset = (id: string | null | undefined): AvatarPreset | null => {
  if (!id) return null;
  const nounId = decodePresetId(id);
  if (nounId === null) return null;
  return PRESETS_BY_NOUN_ID.get(nounId) ?? { nounId, bgClass: bgForSeed(id) };
};

export const defaultAvatarPreset = (seed: string): AvatarPreset => ({
  nounId: (hashString(seed) % NOUN_RANGE) + 1,
  bgClass: bgForSeed(seed),
});

export async function fetchNounSvg(nounId: number): Promise<string> {
  const DOMPurify = (await import("dompurify")).default;
  const response = await fetch(nounImageUrl(nounId));
  if (!response.ok) throw new Error(`Failed to load Noun #${nounId}`);
  const svg = await response.text();
  return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });
}
